import { Edit01, LayersTwo01, Map02 } from "@untitledui/icons";
import { type MouseEvent, type SyntheticEvent, useMemo } from "react";

import { Badge } from "@/components/ui/badges/badges";
import { cx } from "@/utils/cx";

import type { ShadowFloor } from "../../../types/location-map";

import {
	type RowActionItem,
	RowActionsMenu,
} from "../../../components/row-actions-menu";
import { toTitleCase } from "../../../utils/shared/strings";

interface FloorAccordionTitleProps {
	floor: ShadowFloor;
	isActive?: boolean;
	isEditDisabled?: boolean;
	isEditing?: boolean;
	isExpanded: boolean;
	onAddZone?: () => void;
	onEdit?: () => void;
	onToggle: () => void;
	zoneCount: number;
}

const stopAccordionToggle = (event: SyntheticEvent) => {
	event.stopPropagation();
};

export const FloorAccordionTitle = ({
	floor,
	isActive = false,
	isEditDisabled = false,
	isEditing = false,
	isExpanded,
	onAddZone,
	onEdit,
	onToggle,
	zoneCount,
}: FloorAccordionTitleProps) => {
	const zoneCountLabel = `${zoneCount} ${zoneCount === 1 ? "zone" : "zones"}`;
	const showEditControls = isExpanded && !isEditing;

	const menuItems = useMemo((): RowActionItem[] => {
		if (!showEditControls) return [];
		return [
			onEdit
				? {
						icon: Edit01,
						id: "edit-floor",
						isDisabled: isEditDisabled,
						label: "Edit floor",
						onAction: onEdit,
					}
				: null,
			onAddZone
				? {
						icon: Map02,
						id: "add-zone",
						isDisabled: isEditDisabled,
						label: "Add zone",
						onAction: onAddZone,
					}
				: null,
		].filter(Boolean) as RowActionItem[];
	}, [isEditDisabled, onAddZone, onEdit, showEditControls]);

	return (
		<div className="flex w-full items-center justify-between gap-3">
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<LayersTwo01
					aria-hidden
					className={cx(
						"size-4 shrink-0",
						isActive ? "text-brand-secondary" : "text-fg-quaternary",
					)}
				/>
				<button
					aria-current={isActive ? "true" : undefined}
					aria-expanded={isExpanded}
					className="flex min-w-0 flex-1 cursor-pointer flex-col rounded-md text-left outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
					data-active={isActive ? "true" : "false"}
					onClick={onToggle}
					type="button"
				>
					<span className="text-[11px] leading-none font-medium tracking-wide text-tertiary uppercase">
						Floor
					</span>
					<span
						className={cx(
							"mt-0.5 truncate text-sm font-semibold",
							isActive ? "text-brand-secondary" : "text-primary",
						)}
					>
						{toTitleCase(floor.name)}
					</span>
				</button>
			</div>
			<div
				className="flex shrink-0 items-center gap-2"
				onClick={stopAccordionToggle}
				onKeyDown={stopAccordionToggle}
				role="presentation"
			>
				{isEditing ? (
					<span className="text-brand text-xs font-medium">Editing above</span>
				) : menuItems.length > 0 ? (
					<div
						onClick={(event: MouseEvent) => stopAccordionToggle(event)}
						onKeyDown={stopAccordionToggle}
						role="presentation"
					>
						<RowActionsMenu
							ariaLabel={`Actions for ${toTitleCase(floor.name)}`}
							isDisabled={isEditDisabled}
							items={menuItems}
						/>
					</div>
				) : (
					<Badge color="gray" size="sm" type="pill-color">
						{zoneCountLabel}
					</Badge>
				)}
			</div>
		</div>
	);
};
